import React, { useState, useEffect, useMemo } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FaEye, FaShoppingCart, FaCheck, FaClock, FaTimes, FaTruck, FaCreditCard, FaSearch, FaFilter, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import axios from 'axios';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const columnHelper = createColumnHelper();

const PharmacyBranchOrders = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Table states
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        setTitle('أوردرات الفرع');
        fetchOrders();
    }, [setTitle]);

    useEffect(() => {
        fetchOrders();
    }, [currentPage]);

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const getUserRole = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.role;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            const userRole = getUserRole();

            if (!token || userRole !== 'pharmacy_branch') {
                toast.error('غير مصرح لك بالوصول لهذه الصفحة');
                navigate('/');
                return;
            }

            const response = await axios.get(`${BASE_URL}/branch/orders?page=${currentPage}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 200) {
                setOrders(response.data.data[0] || []);
                // تحويل البيانات إلى الشكل المطلوب للباجينيشن
                const paginationData = response.data.data[1] || null;
                if (paginationData) {
                    const formattedPagination = {
                        current_page: paginationData.current_page,
                        last_page: paginationData.total_pages,
                        total: paginationData.total_items,
                        per_page: paginationData.per_page,
                        next_page: paginationData.next_page,
                        prev_page: paginationData.prev_page
                    };
                    setPagination(formattedPagination);
                } else {
                    setPagination(null);
                }
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'فشل في جلب الطلبات');
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب الطلبات');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return <span className="badge bg-success">مكتمل</span>;
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'placed':
                return <span className="badge bg-info">تم الطلب</span>;
            case 'cancelled':
                return <span className="badge bg-danger">ملغي</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const getPaymentStatusBadge = (status) => {
        switch (status) {
            case 'paid':
                return <span className="badge bg-success">مدفوع</span>;
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'failed':
                return <span className="badge bg-danger">فشل</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const getDeliveryStatusBadge = (status) => {
        switch (status) {
            case 'delivered':
                return <span className="badge bg-success">تم التسليم</span>;
            case 'accepted':
                return <span className="badge bg-info">مقبول</span>;
            case 'pending':
                return <span className="badge bg-warning">في الانتظار</span>;
            case 'cancelled':
                return <span className="badge bg-danger">ملغي</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-SA', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            calendar: 'gregory'
        });
    };

    // تعريف الأعمدة
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'رقم الطلب',
                cell: ({ row }) => (
                    <div className="order-number-badge">
                        #{row.index + 1}
                    </div>
                ),
                size: 120,
                minSize: 100,
                maxSize: 150,
            }),
            columnHelper.accessor('client', {
                id: 'client',
                header: 'اسم العميل',
                cell: ({ row }) => (
                    <div className="client-name text-center">
                        {row.original.client?.name || (typeof row.original.client === 'string' ? row.original.client : 'غير محدد')}
                    </div>
                ),
                size: 180,
                minSize: 150,
                maxSize: 250,
            }),
            columnHelper.accessor('client_phone', {
                id: 'client_phone',
                header: 'رقم التليفون',
                cell: ({ row }) => (
                    <div className="phone-display text-center">
                        {row.original.client_phone || 'غير محدد'}
                    </div>
                ),
                size: 140,
                minSize: 120,
                maxSize: 180,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'الحالة',
                cell: ({ row }) => getStatusBadge(row.original.status),
                size: 140,
                minSize: 120,
                maxSize: 180,
            }),
            columnHelper.accessor('payment_status', {
                id: 'payment_status',
                header: 'حالة الدفع',
                cell: ({ row }) => getPaymentStatusBadge(row.original.payment_status),
                size: 140,
                minSize: 120,
                maxSize: 180,
            }),
            columnHelper.accessor('delivery_status', {
                id: 'delivery_status',
                header: 'حالة التسليم',
                cell: ({ row }) => getDeliveryStatusBadge(row.original.delivery_status),
                size: 160,
                minSize: 140,
                maxSize: 200,
            }),
            columnHelper.accessor('total', {
                id: 'total',
                header: 'المجموع',
                cell: ({ row }) => (
                    <div className="total-amount">
                        {row.original.total} ج.م
                    </div>
                ),
                size: 120,
                minSize: 100,
                maxSize: 150,
            }),
            columnHelper.accessor('created_at', {
                id: 'created_at',
                header: 'التاريخ',
                cell: ({ row }) => (
                    <div className="date-display">
                        {formatDate(row.original.created_at)}
                    </div>
                ),
                size: 180,
                minSize: 150,
                maxSize: 220,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => (
                    <div className="action-buttons">
                        <motion.button
                            whileHover={{ scale: 1.05, y: -1 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-view"
                            onClick={() => navigate(`/pharmacy-branch-orders/${row.original.id}`)}
                            title="عرض التفاصيل">
                            <FaEye />
                        </motion.button>
                    </div>
                ),
                size: 100,
                minSize: 80,
                maxSize: 120,
            }),
        ],
        [navigate]
    );

    // إعداد الجدول
    const table = useReactTable({
        data: orders || [],
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const LoadingSpinner = () => (
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="alert alert-warning text-center">
                        <h4>خطأ</h4>
                        <p>{error}</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => window.location.reload()}
                        >
                            إعادة المحاولة
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="mb-0"
                        style={{ color: '#184194', fontSize: '22px' }}
                    >
                        أوردرات الفرع
                    </motion.h2>
                </div>

                {/* Search and Filter Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern mb-4"
                >
                    <div className="card-body p-3">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="search-container position-relative">
                                    <div className="input-group search-input-group">
                                        <span className="input-group-text search-icon">
                                            <FaSearch className="text-white" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control search-input"
                                            placeholder="البحث في الطلبات..."
                                            value={globalFilter ?? ''}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 text-end">
                                <div className="d-flex gap-2 justify-content-end align-items-center">
                                    {(globalFilter || columnFilters.length > 0) && (
                                        <span className="badge bg-info text-white mx-2">
                                            {table.getFilteredRowModel().rows.length} من {orders?.length || 0} نتيجة
                                        </span>
                                    )}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <FaFilter className="mx-1" />
                                        {showFilters ? 'إخفاء المرشحات' : 'إظهار المرشحات'}
                                    </button>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            setGlobalFilter('');
                                            setColumnFilters([]);
                                            setSorting([]);
                                        }}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        مسح الكل
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Advanced Filters */}
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 pt-3 border-top"
                            >
                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">حالة الطلب</label>
                                        <select
                                            className="form-select"
                                            value={columnFilters.find(f => f.id === 'status')?.value ?? ''}
                                            onChange={(e) => {
                                                const statusFilter = e.target.value;
                                                setColumnFilters(prev =>
                                                    prev.filter(f => f.id !== 'status').concat(
                                                        statusFilter ? [{ id: 'status', value: statusFilter }] : []
                                                    )
                                                );
                                            }}
                                        >
                                            <option value="">جميع الحالات</option>
                                            <option value="pending">في الانتظار</option>
                                            <option value="placed">تم الطلب</option>
                                            <option value="cancelled">ملغي</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">حالة الدفع</label>
                                        <select
                                            className="form-select"
                                            value={columnFilters.find(f => f.id === 'payment_status')?.value ?? ''}
                                            onChange={(e) => {
                                                const paymentFilter = e.target.value;
                                                setColumnFilters(prev =>
                                                    prev.filter(f => f.id !== 'payment_status').concat(
                                                        paymentFilter ? [{ id: 'payment_status', value: paymentFilter }] : []
                                                    )
                                                );
                                            }}
                                        >
                                            <option value="">جميع حالات الدفع</option>
                                            <option value="paid">مدفوع</option>
                                            <option value="pending">في الانتظار</option>
                                            <option value="failed">فشل</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">حالة التسليم</label>
                                        <select
                                            className="form-select"
                                            value={columnFilters.find(f => f.id === 'delivery_status')?.value ?? ''}
                                            onChange={(e) => {
                                                const deliveryFilter = e.target.value;
                                                setColumnFilters(prev =>
                                                    prev.filter(f => f.id !== 'delivery_status').concat(
                                                        deliveryFilter ? [{ id: 'delivery_status', value: deliveryFilter }] : []
                                                    )
                                                );
                                            }}
                                        >
                                            <option value="">جميع حالات التسليم</option>
                                            <option value="delivered">تم التسليم</option>
                                            <option value="accepted">مقبول</option>
                                            <option value="pending">في الانتظار</option>
                                            <option value="cancelled">ملغي</option>
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                {/* Modern Data Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern"
                >
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الطلبات...</p>
                            </div>
                        ) : orders && Array.isArray(orders) && orders.length > 0 ? (
                            <div className="table-responsive" style={{
                                overflowX: 'auto',
                                overflowY: 'visible',
                                maxHeight: 'none',
                                width: '100%'
                            }}>
                                <table className="table mb-0 table-modern" style={{
                                    backgroundColor: 'transparent',
                                    '--bs-table-striped-bg': 'transparent',
                                    '--bs-table-hover-bg': 'rgba(0,0,0,0.05)',
                                    width: '100%',
                                    minWidth: '1200px'
                                }}>
                                    <thead className="table-light">
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <th
                                                        key={header.id}
                                                        className="border-0 fw-semibold text-center"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                            userSelect: 'none',
                                                            width: header.getSize()
                                                        }}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {header.column.getCanSort() && (
                                                                <span>
                                                                    {header.column.getIsSorted() === 'asc' ? (
                                                                        <FaSortUp className="text-primary" />
                                                                    ) : header.column.getIsSorted() === 'desc' ? (
                                                                        <FaSortDown className="text-primary" />
                                                                    ) : (
                                                                        <FaSort className="text-muted" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <motion.tr
                                                key={row.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                style={{ backgroundColor: 'transparent' }}
                                                className="table-row-hover"
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="border-0 text-center align-middle"
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            width: cell.column.getSize(),
                                                            verticalAlign: 'middle'
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <div className="alert alert-info">
                                    <h4>لا توجد طلبات</h4>
                                    <p>لم يتم العثور على أي طلبات.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* API Pagination */}
                {pagination && pagination.last_page > 1 && (
                    <div className="card-footer bg-light py-2 px-2">
                        <div className="row align-items-center g-0">
                            <div className="col-md-6 px-2">
                                <div className="d-flex align-items-center gap-1 flex-wrap">
                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                    <div className="input-group" style={{ width: 'auto' }}>
                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                            #
                                        </span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={currentPage}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                if (!isNaN(value) && value > 0) {
                                                    const totalPages = pagination?.last_page || 1;
                                                    if (value <= totalPages) {
                                                        handlePageChange(value);
                                                    }
                                                }
                                            }}
                                            min="1"
                                            max={pagination?.last_page || 1}
                                            style={{ width: '60px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 px-2">
                                <div className="d-flex justify-content-end gap-1 flex-wrap">
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage <= 1}
                                    >
                                        الأولى
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                    >
                                        السابقة
                                    </button>
                                    <span className="d-flex align-items-center px-3">
                                        صفحة {pagination.current_page || currentPage} من {pagination.last_page || 1}
                                    </span>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage >= (pagination.last_page || 1)}
                                    >
                                        التالية
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(pagination.last_page || 1)}
                                        disabled={currentPage >= (pagination.last_page || 1)}
                                    >
                                        الأخيرة
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PharmacyBranchOrders;

// CSS Styles
const styles = `
    .card-modern {
        border: none;
        border-radius: 12px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        background: white;
    }

    .search-container {
        position: relative;
    }

    .search-input-group {
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .search-icon {
        background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
        border: none;
        color: white;
        padding: 0.75rem 1rem;
    }

    .search-input {
        border: none;
        padding: 0.75rem 1rem;
        font-size: 0.9rem;
        background: #f8fafc;
    }

    .search-input:focus {
        box-shadow: none;
        background: white;
    }

    .table-modern {
        background: white;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        border: none;
    }

    .table-modern thead {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
    }

    .table-modern thead th {
        border: none;
        padding: 1rem 0.75rem;
        font-weight: 600;
        font-size: 0.9rem;
        text-align: center;
    }

    .table-modern tbody tr {
        border-bottom: 1px solid #f1f5f9;
        transition: all 0.3s ease;
    }

    .table-modern tbody tr:hover {
        background-color: #f8fafc;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    .table-modern tbody td {
        border: none;
        padding: 1rem 0.75rem;
        vertical-align: middle;
        text-align: center;
    }

    .order-number-badge {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-weight: 600;
        font-size: 0.85rem;
        display: inline-block;
        min-width: 60px;
    }

    .client-name {
        font-weight: 500;
        color: #374151;
        max-width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 auto;
    }

    .total-amount {
        font-weight: 600;
        color: #059669;
        font-size: 1rem;
    }

    .date-display {
        background: #f8fafc;
        color: #64748b;
        padding: 0.5rem 1rem;
        border-radius: 8px;
        font-size: 0.875rem;
        border: 1px solid #e2e8f0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 auto;
        line-height: 1.4;
        max-width: 200px;
    }

    .phone-display {
        background: #eff6ff;
        color: #1e40af;
        padding: 0.5rem 1rem;
        border-radius: 8px;
        font-size: 0.875rem;
        border: 1px solid #bfdbfe;
        font-weight: 500;
        direction: ltr;
    }

    .action-buttons {
        display: flex !important;
        gap: 0.5rem !important;
        justify-content: center !important;
        flex-direction: row !important;
        flex-wrap: nowrap !important;
        max-width: 120px !important;
        margin: 0 auto !important;
        padding: 0 !important;
        align-items: center !important;
    }

    .btn-view {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        color: white !important;
        border: none !important;
        padding: 0.5rem !important;
        border-radius: 8px !important;
        font-size: 0.9rem !important;
        font-weight: 500 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: all 0.3s ease !important;
        box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3) !important;
        width: 40px !important;
        height: 40px !important;
        min-width: 40px !important;
        max-width: 40px !important;
    }

    .btn-view:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4) !important;
    }

    .badge {
        font-size: 0.75rem;
        padding: 0.5rem 0.75rem;
        border-radius: 20px;
        font-weight: 500;
    }

    .table-responsive {
        overflow-x: auto !important;
        scrollbar-width: auto;
        scrollbar-color: #cbd5e1 #f1f5f9;
    }

    .table-responsive::-webkit-scrollbar {
        height: 8px;
    }

    .table-responsive::-webkit-scrollbar-track {
        background: #f1f5f9;
        border-radius: 4px;
    }

    .table-responsive::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
    }

    .table-responsive::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
    }

    .table-responsive::-webkit-scrollbar-corner {
        background: #f1f5f9;
    }

    /* Pagination Styling */
    .card-footer {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
        border-top: 1px solid #e9ecef !important;
        padding: 1rem 1.5rem !important;
    }

    .btn-outline-secondary {
        border-color: #6c757d !important;
        color: #6c757d !important;
        transition: all 0.3s ease !important;
    }

    .btn-outline-secondary:hover:not(:disabled) {
        background-color: #6c757d !important;
        border-color: #6c757d !important;
        color: white !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 2px 4px rgba(108, 117, 125, 0.3) !important;
    }

    .btn-outline-secondary:disabled {
        opacity: 0.5 !important;
        cursor: not-allowed !important;
    }

    .input-group-text {
        border-color: #dee2e6 !important;
        color: #6c757d !important;
        font-weight: 600 !important;
    }

    .form-control-sm {
        border: 2px solid #e9ecef !important;
        border-radius: 6px !important;
        text-align: center !important;
        font-weight: 600 !important;
        transition: all 0.3s ease !important;
    }

    .form-control-sm:focus {
        border-color: #184194 !important;
        box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25) !important;
        transform: translateY(-1px) !important;
    }

    @media (max-width: 768px) {
        .search-container {
            margin-bottom: 1rem;
        }
        
        .table-modern thead th,
        .table-modern tbody td {
            padding: 0.75rem 0.5rem;
            font-size: 0.8rem;
        }
        
        .action-buttons {
            flex-direction: column !important;
            gap: 0.25rem !important;
        }
        
        .btn-view {
            width: 35px !important;
            height: 35px !important;
            min-width: 35px !important;
            max-width: 35px !important;
        }

        .card-footer .row {
            flex-direction: column;
            gap: 1rem;
        }

        .card-footer .col-md-6 {
            width: 100%;
            text-align: center;
        }

        .d-flex.gap-2 {
            justify-content: center;
            flex-wrap: wrap;
        }
    }
`;

// Add styles to document head
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}
